import { css } from 'aphrodite/no-important';
import React, { FC } from 'react';
// util
import { ChatIcon, EmailIcon } from '../../../util/icons';
// components
import { Product } from '../Product/Product';
// models
import { ExhibitDetailsResource, PartnerDetailProps } from './PartnerDetail.types';
// styles
import { style } from './style';

export const PartnerDetail: FC<PartnerDetailProps> = ({
  details,
  technicalTrackSessions,
  isPreview,
  isLiveChatWindowOpen,
  onEmailButtonClick,
  onChatButtonClick,
  onBackButtonClick,
  onResourceClick,
  onSocialLinkClick,
  backButtonText = 'Back to Partner Pavilion Lobby',
}: PartnerDetailProps) => {
  const styles = style();
  const { basic_info, contact_info, resource_center, live_chat } = details;
  const hasTechnicalTrackSessions = !!technicalTrackSessions && technicalTrackSessions.length;

  const handleChatClick = () => {
    if (onChatButtonClick) {
      onChatButtonClick();
    }
  };

  const handleEmailClick = () => {
    if (onEmailButtonClick) {
      onEmailButtonClick();
    }
  };

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (onBackButtonClick) {
      onBackButtonClick();
    }
  };

  const handleSocialClick = (label: string) => {
    if (onSocialLinkClick) {
      onSocialLinkClick(label);
    }
  };

  const handleResourceClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    resource: { productTitle: string; productReadMoreUrl: string }
  ) => {
    if (onResourceClick) {
      onResourceClick(resource.productTitle, resource.productReadMoreUrl);
    }
  };

  const EmailBadge = () => (
    <div className={css(styles.chatBadgeContent)}>
      <div className={css(styles.chatBadgeText)}>
        <EmailIcon></EmailIcon>
        <span>Got Questions?</span>
      </div>
      <a className={css(styles.chatButton)} href={`mailto:${live_chat.chat_offline_email}`} onClick={handleEmailClick}>
        Send us your questions
      </a>
    </div>
  );

  const ChatBadge = () => (
    <div className={css(styles.chatBadgeContent)}>
      <div className={css(styles.chatBadgeText)}>
        <ChatIcon></ChatIcon>
        <span>LIVE CHAT</span>
      </div>
      <button className={css(styles.chatButton)} type='button' onClick={handleChatClick}>
        Click to Start Live Chat
      </button>
    </div>
  );

  const ResourceCenter = () => {
    const resources = resource_center.map((resource: ExhibitDetailsResource, index: number) => {
      return (
        <Product
          productTitle={resource.resource_title}
          productInfo={resource.resource_description}
          productReadMoreUrl={resource.resource_link}
          readMoreLabelText='Learn More'
          onResourceClick={handleResourceClick}
          key={index}></Product>
      );
    });

    return <>{resources}</>;
  };

  const Contacts = () => {
    const contacts = contact_info.contacts.map((contact: any, index: number) => {
      return (
        <div className={css(styles.contactInfo)} key={index}>
          <h4>{contact.contact_name}</h4>
          {contact.contact_phone && <span>{contact.contact_phone}</span>}
          <span>{contact.contact_email}</span>
        </div>
      );
    });
    return <div className={css(styles.contactInfoContainer)}>{contacts}</div>;
  };

  const SocialLinks = () => {
    const { facebook_url, twitter_url, linkedin_url } = contact_info;

    if (facebook_url || twitter_url || linkedin_url) {
      return (
        <div className={css(styles.socialLinks)}>
          <div className={css(styles.socialLinksInner)}>
            {linkedin_url && (
              <a href={linkedin_url} target='_blank' rel='noreferrer' onClick={() => handleSocialClick('LinkedIn')}>
                <img
                  src='https://ts-cms-production.votenow.tv/campaign/10/08/1008023/16149796196042a2234c43f7.72769699.png'
                  alt='LinkedIn'
                />
              </a>
            )}
            {facebook_url && (
              <a href={facebook_url} target='_blank' rel='noreferrer' onClick={() => handleSocialClick('Facebook')}>
                <img
                  src='https://ts-cms-production.votenow.tv/campaign/10/08/1008023/16149796066042a21675ce96.56863296.png'
                  alt='Facebook'
                />
              </a>
            )}
            {twitter_url && (
              <a href={twitter_url} target='_blank' rel='noreferrer' onClick={() => handleSocialClick('Twitter')}>
                <img
                  src='https://ts-cms-production.votenow.tv/campaign/10/08/1008023/16149796126042a21c5f5b72.13968295.png'
                  alt='Twitter'
                />
              </a>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <main className={css(styles.main)}>
      <div className={css(styles.topSection)}>
        <div className={css(styles.container)}>
          <nav className={css(styles.nav)}>
            <a href='' onClick={handleBackClick}>
              <span className={css(styles.backButton)}>{backButtonText}</span>
            </a>
          </nav>
          <section className={css(styles.companySection)}>
            <header className={css(styles.header)}>
              <img className={css(styles.companyLogo)} src={basic_info.logo} alt={`Exhibit Logo`} />
            </header>
            <div className={css(styles.columnWrapper)}>
              <article className={css(styles.aboutArticle)}>
                <h2 className={css(styles.h2)}>About</h2>
                <div dangerouslySetInnerHTML={{ __html: basic_info.about }}></div>
              </article>

              <aside className={css(styles.sidebarContent)}>
                <div className={css(styles.chatBadgeContainer)}>
                  <div className={css(styles.chatBadge)}>
                    {isLiveChatWindowOpen && live_chat.is_participating ? <ChatBadge /> : <EmailBadge />}
                  </div>
                </div>

                {/* Contact Section */}
                <div className={css(styles.contactSection)}>
                  <div className={css(styles.contactList)}>
                    <h3 className={css(styles.h3)}>Contact</h3>
                    <Contacts />
                  </div>
                  <SocialLinks />
                </div>
              </aside>
            </div>
          </section>
        </div>
      </div>
      {hasTechnicalTrackSessions && (
        <section className={css(styles.technicalTrackSessionsSection)}>
          <div className={css(styles.technicalTrackSessionsContent)}>
            <h2 className={css(styles.h2)}>Technical Track Sessions</h2>
            <div className={css(styles.technicalTrackSessionsGrid)}>{technicalTrackSessions}</div>
          </div>
        </section>
      )}
      <div className={css(styles.bottomSection)}>
        <div className={css(styles.container)}>
          <section>
            <h2>Resource Center</h2>
            <div className={css(styles.grid)}>
              <ResourceCenter />
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};
